import { useState, useEffect, useCallback } from "react";
import { toNumberFromSatoshi } from "../sdk/utils/math";
import { normalizeProtobufTimestamp } from "../sdk/utils/time";
import { useSubscription } from "../context/SubscriptionContext";
import { MultiFeedKlineData } from "../sdk/types/api";
import { KlineConfig, KlineData } from "../sdk/types/data";

/**
 * Hook for managing kline (OHLC candlestick) data with WebSocket real-time updates
 * Maintains a rolling buffer of kline data points (max 100 by default)
 */
export function useKline(config: KlineConfig) {
  const maxDataPoints = config.maxDataPoints || 100;
  const [klineData, setKlineData] = useState<KlineData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);


  /**
   * Handle kline update from WebSocket (SubscriptionCallback receives unknown)
   */
  const handleKlineUpdate = useCallback(
    (payload: unknown) => {
      const data = payload as MultiFeedKlineData | null | undefined;
      if (!data) {
        console.warn(
          "[useKline] handleKlineUpdate received null/undefined data",
        );
        return;
      }

      try {
        // Convert protobuf timestamps to JavaScript timestamps (milliseconds)
        const openTime = normalizeProtobufTimestamp(data.openTime, Date.now());
        const closeTime = normalizeProtobufTimestamp(data.closeTime, openTime);
        const timestamp = normalizeProtobufTimestamp(data.timestamp, openTime);

        // Convert satoshi strings to decimal numbers
        // Use utility function from math.ts for consistent conversion
        const kline: KlineData = {
          marketIndex: config.marketId,
          symbol: data.symbol,
          interval: data.interval || config.interval,
          openTime,
          closeTime,
          open: toNumberFromSatoshi(data.open),
          high: toNumberFromSatoshi(data.high),
          low: toNumberFromSatoshi(data.low),
          close: toNumberFromSatoshi(data.close),
          volume: toNumberFromSatoshi(data.volume),
          tradeCount: data.tradeCount ?? 0,
          timestamp,
          raw: data, // Store raw data for modal display
        };

        setKlineData((prev) => {
          const updated = [...prev, kline];
          // Keep only the most recent maxDataPoints
          if (updated.length > maxDataPoints) {
            return updated.slice(-maxDataPoints);
          }
          return updated;
        });
      } catch (err) {
        console.error("[useKline] Error processing kline update:", err);
        setError(
          err instanceof Error ? err.message : "Failed to process kline data",
        );
      }
    },
    [config.marketId, config.interval, maxDataPoints],
  );

  const { subscribe, unsubscribe, isConnected: contextConnected } = useSubscription();

  useEffect(() => {
    setIsConnected(contextConnected);
    setLoading(false);
    if (!config.marketId) return;
    const key = `kline:${config.marketId}:${config.interval}`;
    const callback = (data: unknown) => handleKlineUpdate(data);
    subscribe(key, callback);
    return () => unsubscribe(key, callback);
  }, [
    config.marketId,
    config.interval,
    contextConnected,
    subscribe,
    unsubscribe,
    handleKlineUpdate,
  ]);

  return {
    klineData,
    loading,
    error,
    isConnected,
  };
}
